import { useAuth0 } from '@auth0/auth0-react';
import { useContext, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router';
import TopicPicker from 'components/Common/TopicPicker';
import { useChangeKeywordsListsOrder } from 'components/Dashboard/hooks/useSearchBarUiFunctions';
import { useSetListsIds } from 'components/Dashboard/MainSearchBar/setListIds';
import useTopicOrClientWord from 'components/Dashboard/hooks/useTopicOrClientWord';
import {
  relevantActivityFalseCondition,
  searchIncludesTitleConditional,
} from 'components/Dashboard/StakeHolders/searchIncludesTitle';
import useIsMobile from 'components/Dashboard/utilities/useIsMobile';
import { store } from 'components/Store';
import useTopicPickerUrlSync from 'components/Common/TopicPicker/useTopicPickerUrlSync';

const StakeholderTopicPicker = (props) => {
  const {
    localTopic,
    setLocalTopic,
    searchPageQuery,
    keywordsLists,
    seeAllActivity,
    StakeholderInformation,
    setShowPopup,
    stakeholderSearchCondition,
    activeTabState,
    onDropdownStateChange,
    disabled = false,
  } = props;

  const globalState = useContext(store);
  const { state, dispatch } = globalState;
  const { search, activePlan } = state;
  const { enableStakeholders } = activePlan ?? {};

  const { setListsIds } = useSetListsIds();
  const { changeKeywordsListsOrder } = useChangeKeywordsListsOrder({ keywordsLists });
  const keywordsListsToUse = changeKeywordsListsOrder();

  const { activeTab, setActiveTab } = activeTabState ?? {};
  const { isAuthenticated } = useAuth0();
  const navigate = useNavigate();
  const location = useLocation();
  const isMobileTablet = useIsMobile(1310);
  const { topicOrClientWord } = useTopicOrClientWord();

  const { officalName } = StakeholderInformation ?? {};
  const shouldForceSearchInput =
    searchIncludesTitleConditional(officalName) || relevantActivityFalseCondition() || !enableStakeholders;

  const showTopicCases = useMemo(() => {
    if (shouldForceSearchInput) {
      return false;
    }
    return !!localTopic && !searchPageQuery;
  }, [localTopic, searchPageQuery, shouldForceSearchInput]);

  const mainSearchValue = shouldForceSearchInput ? '' : searchPageQuery ?? '';
  const placeholder = isMobileTablet ? 'Search activity' : `Search by keyword or ${topicOrClientWord}`;
  const syncTopicPickerToUrl = useTopicPickerUrlSync({
    navigate,
    location,
    setListsIds,
    setSelectedTopic: setLocalTopic,
    replace: true,
    keepHash: false,
    mapTopicParamValue: (topic) => (topic?.id === null ? 'all' : topic?.id),
  });

  const searchFunction = ({ freeSearch, topic }) => {
    if (disabled) {
      return;
    }
    if (!stakeholderSearchCondition) {
      setShowPopup(true);
      return;
    }

    syncTopicPickerToUrl({ topic, freeSearch });

    setTimeout(() => {
      dispatch({
        type: 'MODIFY_SECTION',
        parameter: 'search',
        value: { ...search, query: freeSearch ?? '' },
      });
      dispatch({
        type: 'MODIFY_SECTION',
        parameter: 'stakeholderMobileSection',
        value: 'activity',
      });
    }, 10);

    if (activeTab !== 'Activity' && !isAuthenticated) {
      setActiveTab('Activity');
    }
  };

  return (
    <TopicPicker
      variant='stakeholder'
      allowFreeSearch={true}
      keywordsLists={keywordsListsToUse}
      defaultTopic={localTopic}
      selectedTopic={localTopic}
      showTopicCases={showTopicCases}
      mainSearchValue={mainSearchValue}
      searchFunction={searchFunction}
      closeButtonFunction={() => {
        if (disabled) {
          return;
        }
        seeAllActivity();
        setLocalTopic(null);
      }}
      closeButtonMessage='See all activity'
      closeButtonPosition='top'
      placeholder={placeholder}
      recentItemsCondition={true}
      limitToCountOfItems={3.7}
      showMasterTopicIcon={true}
      alwaysShowSelectedMark={true}
      onDropdownStateChange={onDropdownStateChange}
      emptyInputStatus={shouldForceSearchInput || !localTopic}
      disabled={disabled}
    />
  );
};

export default StakeholderTopicPicker;
